import { DependencyList, useEffect } from 'react';
import { useAppDispatch } from 'app/hooks';
import { AppDispatch } from 'app/store';

/**
 * Ejecuta dispatches en un efecto sin necesidad de declarar `dispatch` en el
 * componente si solo se usa dentro del efecto.
 *
 * @param effect - Función que recibe `dispatch` y opcionalmente devuelve un cleanup.
 * @param deps - Dependencias del efecto. Por defecto `[]` (solo al montar).
 *   Pásalas cuando el efecto deba re-ejecutarse al cambiar algo — típicamente el
 *   `:id` de la ruta: React Router reutiliza el mismo componente al navegar entre
 *   dos entidades del mismo tipo, así que con `[]` la recarga nunca se dispararía.
 *
 * @example Solo al montar (sin dependencias que cambien)
 * useDispatchOnMount((dispatch) => {
 *   dispatch(getServiceTypeAsync());
 * });
 *
 * @example Recargar al cambiar el :id de la ruta
 * const { id } = useParams();
 * useDispatchOnMount((dispatch) => {
 *   dispatch(resetDevice());
 *   dispatch(getDeviceAsync(id));
 * }, [id]);
 *
 * @example Con cleanup al desmontar
 * useDispatchOnMount((dispatch) => {
 *   dispatch(startPolling());
 *   return () => dispatch(stopPolling());
 * });
 */
export const useDispatchOnMount = (
  effect: (dispatch: AppDispatch) => void | (() => void),
  deps: DependencyList = [],
) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const cleanup = effect(dispatch);
    return cleanup ?? undefined;
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};
